import React, { useState } from "react";
import { withTranslation } from "react-i18next";
import StatusBadge from "@components/statusbadge/StatusBadge";
import ProgressBar from "@components/progressBar/ProgressBar";
import Loadable from "@react-loadable/revised";
import moment from "moment";
import convertToByte from "@cores/convertToByte";
import ObjectIcon from "./ObjectIcon";
import { UPLOAD_STATUS } from "@constants";

const LoadableDeleteConfirmModal = Loadable({
    loader: () => import("./DeleteConfirm"),
    loading: () => null,
});

const AssetUploadQueueList = (props) => {
    const { t, uploadQueueList, showStatusControl } = props;
    const [searchStatuses, setSearchStatuses] = useState(Object.keys(UPLOAD_STATUS));
    const [deleteActive, setDeleteActive] = useState(false);
    const [cancelKey, setCancelKey] = useState(null);

    const filteredQueue =
        uploadQueueList.length === 0
            ? uploadQueueList
            : uploadQueueList.filter((v) => {
                  return searchStatuses.indexOf(v.status) > -1;
              });

    const onClickRetry = (key) => {
        const { onRetry } = props;
        onRetry(key);
    };

    const onClickCancel = async (key) => {
        const { onCancel } = props;
        onCancel(key);
    };

    const openCancelModal = (key) => {
        setDeleteActive(true);
        setCancelKey(key);
    };

    const onConfirmCancelModal = () => {
        onClickCancel(cancelKey);
        setCancelKey(null);
    };

    const onCloseCancelModal = () => {
        setDeleteActive(false);
        setCancelKey(null);
    };

    const onClickToggleStatus = (status, isChecked) => {
        setSearchStatuses(isChecked ? searchStatuses.concat(status) : searchStatuses.filter((v) => v !== status));
    };

    const onClickToggleFileListExpander = (key) => {
        const { onToggleFileList } = props;
        onToggleFileList(key);
    };

    const calcTotalUploadPercentage = (files) => {
        if (!files || files.length === 0) {
            return 0;
        }
        const uploadedPercentageSum = files && files.reduce((acc, curr) => acc + (curr.uploadedPercentage || 0), 0);
        return uploadedPercentageSum / files.length;
    };

    for (let item of filteredQueue) {
        // 업로드 행위에 대한 폴더와 파일갯수를 표현하기 위한 로직
        if (item.isFolder) {
            let folders = new Map();
            let totalFileSize = 0;
            let otherFilesLength = 0;
            item.files.forEach((file) => {
                let reg = new RegExp(`^\/[^\/\"\x80-\xff\^\}\%\`\'\"\>\[\~\<\#\|]+\/`);
                let findRootFolder =
                    file.fullPath.match(reg) || (file.fullPath === "/" && file.isFolder && [file.name]) || [];
                let foundFolder = folders.get(findRootFolder[0]);
                // console.log('findRootFolder', findRootFolder[0])
                if (findRootFolder.length > 0 && !foundFolder) {
                    folders.set(findRootFolder[0].split("/")[1], 1);
                } else {
                    if (!foundFolder) {
                        otherFilesLength += 1;
                    }
                    // console.log('other hand')
                }
                totalFileSize += file.size;
            });
            folders = Array.from(folders.keys());
            // console.log('folders', folders)
            item.mainFile = folders[0];
            item.fileLength = folders.length + otherFilesLength;
            item.totalSize = totalFileSize;
        } else {
            item.mainFile = item.files[0].name;
            item.fileLength = item.files.length;
            item.totalSize = item.files[0].size;
        }
    }

    // console.log('filteredQueue', filteredQueue)
    // console.log('uploadQueueList', uploadQueueList)
    return (
        <React.Fragment>
            {deleteActive && (
                <LoadableDeleteConfirmModal
                    title={`Upload Cancel`}
                    messages={[`Upload did not complete.`, `Are you sure you want to cancel all uploads in progress?`]}
                    confirmButtonText={`Upload Cancel`}
                    cancelButtonText={`Upload Continue`}
                    onClickClose={onCloseCancelModal}
                    onClickDelete={onConfirmCancelModal}
                />
            )}
            {uploadQueueList.length > 0 ? (
                <React.Fragment>
                    {showStatusControl ? (
                        <div className={"card "}>
                            <div className={"card-body"}>
                                <div className={"row row-column flex-wrap"}>
                                    {Object.keys(UPLOAD_STATUS).map((key, i) => (
                                        <div key={i} className={`col col-2 ${key}`}>
                                            <label
                                                className={`form-checkbox form-checkbox-status ${key}`}
                                                htmlFor={`chkSearchStatus${i}`}
                                            >
                                                <input
                                                    id={`chkSearchStatus${i}`}
                                                    className={"form-check-input"}
                                                    type={"checkbox"}
                                                    readOnly
                                                    value={key}
                                                    checked={searchStatuses.indexOf(UPLOAD_STATUS[key]) > -1}
                                                    onClick={(e) => {
                                                        onClickToggleStatus(key, e.target.checked);
                                                    }}
                                                />
                                                <i className={"input-helper"}></i>
                                                <strong>{t(`common::label::${key}`)}</strong>{" "}
                                                <span className={"text-primary"}>
                                                    (
                                                    {
                                                        uploadQueueList.filter((v) => UPLOAD_STATUS[key] === v.status)
                                                            .length
                                                    }
                                                    )
                                                </span>
                                            </label>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    ) : (
                        ""
                    )}

                    {filteredQueue.length === 0 ? (
                        <div className={"empty box-border box-border-radius"}>
                            <i className={"sprite sprite-nojob"}></i>
                            <p>
                                {t(`common::msg::No {{name}} searched.`, `No {{name}} searched.`, {
                                    name: t(`common::label::Files`, `Files`),
                                })}
                            </p>
                        </div>
                    ) : (
                        <div className="list-process">
                            <ul>
                                {filteredQueue.map((q, k) => (
                                    <li key={k}>
                                        <div className="head">
                                            <div className="flex-column align-items-start">
                                                <span className="status-flag">
                                                    <StatusBadge status={q.status} desiredStatus={q.desiredStatus} />
                                                    {q.status === "COMPLETE" &&
                                                        q.files.filter((item) => item.status === "ERROR").length >
                                                            0 && (
                                                            <span className="error">
                                                                <i
                                                                    className="sprite sprite-error"
                                                                    data-tooltip={"Upload Failed."}
                                                                ></i>
                                                            </span>
                                                        )}
                                                </span>
                                                <span className="text-secondary">
                                                    {q.bucketPath
                                                        ? `${q.bucketPath.bucket ? q.bucketPath.bucket + "/" : ""}${
                                                              q.bucketPath.folderPath
                                                                  ? q.bucketPath.folderPath + "/"
                                                                  : ""
                                                          }`
                                                        : ""}
                                                </span>
                                            </div>
                                            <div>
                                                {q.status === UPLOAD_STATUS.CANCELED ||
                                                q.status === UPLOAD_STATUS.ERROR ? (
                                                    <button
                                                        type={"button"}
                                                        className={"btn btn-icon btn-sm"}
                                                        data-tooltip={t(`common::label::Retry`, `Retry`)}
                                                        onClick={(e) => {
                                                            onClickRetry(q.key);
                                                        }}
                                                    >
                                                        <i className={"sprite sprite-retry"}></i>
                                                        <span className="hidden">
                                                            {t(`common::label::Retry`, `Retry`)}
                                                        </span>
                                                    </button>
                                                ) : (
                                                    <React.Fragment>
                                                        {q.status === UPLOAD_STATUS.UPLOADING &&
                                                            calcTotalUploadPercentage(q.files) >= 0 && (
                                                                <button
                                                                    type={"button"}
                                                                    className={"btn btn-icon btn-sm"}
                                                                    data-tooltip={t(`common::label::Cancel`, `Cancel`)}
                                                                    onClick={(e) => {
                                                                        openCancelModal(q.key);
                                                                    }}
                                                                >
                                                                    <i className={"sprite sprite-cancel"}></i>
                                                                    <span className="hidden">
                                                                        {t(`common::label::Cancel`, `Cancel`)}
                                                                    </span>
                                                                </button>
                                                            )}
                                                    </React.Fragment>
                                                )}
                                            </div>
                                        </div>
                                        <div className={"date"}>
                                            {q.created ? (
                                                <dl>
                                                    <dt>{`Create date`}</dt>
                                                    <dd>{`${moment(q.created).format("YYYY-MM-DD HH:mm:ss")}`}</dd>
                                                </dl>
                                            ) : (
                                                ""
                                            )}
                                            {q.uploaded ? (
                                                <dl>
                                                    <dt>{`Complete date`}</dt>
                                                    <dd>{`${moment(q.uploaded).format("YYYY-MM-DD HH:mm:ss")}`}</dd>
                                                </dl>
                                            ) : (
                                                ""
                                            )}
                                        </div>

                                        <div className="list">
                                            {(q.files.length > 1 || (q.isFolder && q.files.length > 0)) && (
                                                <button
                                                    type="button"
                                                    className="btn btn-icon-solid btn-fold"
                                                    onClick={(e) => {
                                                        if (q.files.length > 0) {
                                                            onClickToggleFileListExpander(q.key);
                                                        }
                                                    }}
                                                >
                                                    <i
                                                        className={
                                                            "sprite " +
                                                            (q.isFolded ? "sprite-angle-down" : "sprite-angle-up")
                                                        }
                                                    ></i>
                                                </button>
                                            )}
                                            {q.isFolded && (
                                                <div className="file">
                                                    <div className="file-info">
                                                        {q.files[0] && (
                                                            <React.Fragment>
                                                                <span className="file-type">
                                                                    <ObjectIcon
                                                                        path={q.files[0].name}
                                                                        isFolder={q.isFolder || q.files[0].isFolder}
                                                                    />
                                                                </span>
                                                                <span className="file-name">
                                                                    <strong className="ellipsis">{q.mainFile}</strong>
                                                                </span>
                                                                {q.status !== UPLOAD_STATUS.ERROR && (
                                                                    <span className={"file-size"}>
                                                                        {q.fileLength > 1
                                                                            ? t(
                                                                                  `common::msg::And {{count}} Other - Total {{total}}`,
                                                                                  `And {{count}} Other - Total {{total}}`,
                                                                                  {
                                                                                      count: q.fileLength - 1,
                                                                                      total: convertToByte(q.totalSize),
                                                                                  },
                                                                              )
                                                                            : t(
                                                                                  `common::label:: - Total {{totalSize}}`,
                                                                                  ` - Total {{totalSize}}`,
                                                                                  {
                                                                                      totalSize: convertToByte(
                                                                                          q.totalSize,
                                                                                      ),
                                                                                  },
                                                                              )}
                                                                    </span>
                                                                )}
                                                            </React.Fragment>
                                                        )}
                                                    </div>
                                                    <div className="file-status">
                                                        {q.status === UPLOAD_STATUS.UPLOADING ? (
                                                            <React.Fragment>
                                                                {calcTotalUploadPercentage(q.files) <= 0 ? (
                                                                    <small>
                                                                        <i className={"sprite sprite-progressing"}></i>{" "}
                                                                        {t(
                                                                            `common::msg::Pending to upload...`,
                                                                            `Pending to upload...`,
                                                                        )}
                                                                    </small>
                                                                ) : (
                                                                    <ProgressBar
                                                                        status={q.status}
                                                                        length={1}
                                                                        className={"bg-secondary"}
                                                                        progress={calcTotalUploadPercentage(q.files)}
                                                                    />
                                                                )}
                                                            </React.Fragment>
                                                        ) : (
                                                            ""
                                                        )}

                                                        {q.status === UPLOAD_STATUS.ERROR && (
                                                            <React.Fragment>
                                                                <small className={"text-danger"}>
                                                                    {`${t(`common::label::Uploading`, `Uploading`)} ${t(
                                                                        `common::label::error`,
                                                                        `error`,
                                                                    )}.`}
                                                                </small>
                                                            </React.Fragment>
                                                        )}

                                                        {q.status === UPLOAD_STATUS.CANCELED && (
                                                            <React.Fragment>
                                                                <small className={"text-cancel"}>
                                                                    {`${t(`common::label::Uploading`, `Uploading`)} ${t(
                                                                        `common::label::canceled`,
                                                                        `canceled`,
                                                                    )}.`}
                                                                </small>
                                                            </React.Fragment>
                                                        )}

                                                        {q.status === UPLOAD_STATUS.COMPLETE &&
                                                            q.files.filter((item) => item.status === "ERROR").length ===
                                                                0 && (
                                                                <React.Fragment>
                                                                    <small className={"text-success"}>
                                                                        {`${t(
                                                                            `common::label::Uploading`,
                                                                            `Uploading`,
                                                                        )} ${t(
                                                                            `common::label::complete`,
                                                                            `complete`,
                                                                        )}.`}
                                                                    </small>
                                                                </React.Fragment>
                                                            )}

                                                        {q.status === UPLOAD_STATUS.COMPLETE &&
                                                            q.files.filter((item) => item.status === "ERROR").length >
                                                                0 && (
                                                                <React.Fragment>
                                                                    <small className={"text-danger"}>
                                                                        {t(
                                                                            `common::label::Partial {{type}} to completed.`,
                                                                            `Partial {{type}} to completed.`,
                                                                            {
                                                                                type: `${t(
                                                                                    `common::label::Upload`,
                                                                                    `Upload`,
                                                                                )}`,
                                                                            },
                                                                        )}
                                                                    </small>
                                                                </React.Fragment>
                                                            )}
                                                    </div>
                                                </div>
                                            )}
                                            {!q.isFolded && q.status === UPLOAD_STATUS.CANCELED && (
                                                <div className="file">
                                                    <div className="file-status">
                                                        <small className={"text-cancel"}>
                                                            {`${t(`common::label::Uploading`, `Uploading`)} ${t(
                                                                `common::label::canceled`,
                                                                `canceled`,
                                                            )}.`}
                                                        </small>
                                                    </div>
                                                </div>
                                            )}
                                            {!q.isFolded &&
                                                q.status === "COMPLETE" &&
                                                q.files.filter((item) => item.status === "ERROR").length > 0 && (
                                                    <div className="file">
                                                        <div className="file-status">
                                                            <small className={"text-danger"}>
                                                                {t(
                                                                    `common::label::Partial {{type}} to completed.`,
                                                                    `Partial {{type}} to completed.`,
                                                                    { type: `${t(`common::label::Upload`, `Upload`)}` },
                                                                )}
                                                            </small>
                                                        </div>
                                                    </div>
                                                )}
                                            {!q.isFolded &&
                                                q.files.map((file, i) => (
                                                    <React.Fragment key={i}>
                                                        <div className="file">
                                                            <div className="file-info">
                                                                <span className="file-type">
                                                                    <ObjectIcon
                                                                        path={file.name}
                                                                        isFolder={file.isFolder}
                                                                    />
                                                                </span>
                                                                <span className="file-name">
                                                                    <strong className="ellipsis">
                                                                        {file.fullPath === "/"
                                                                            ? file.name
                                                                            : file.fullPath[0] === "/"
                                                                            ? file.fullPath.includes(file.name)
                                                                                ? file.fullPath.slice(1)
                                                                                : `${file.fullPath.slice(1)}${
                                                                                      file.name
                                                                                  }`
                                                                            : file.fullPath.includes(file.name)
                                                                            ? file.fullPath
                                                                            : `${file.fullPath}${file.name}`}
                                                                    </strong>
                                                                </span>
                                                                <span className={"file-size"}>
                                                                    {t(
                                                                        "common::label:: - Total {{totalSize}}",
                                                                        ` - Total {{totalSize}}`,
                                                                        {
                                                                            totalSize: convertToByte(file.size),
                                                                        },
                                                                    )}
                                                                </span>
                                                                {file.status === "CANCELED" && (
                                                                    <span className={"file-error"}>
                                                                        <i className="sprite sprite-canceled"></i>
                                                                    </span>
                                                                )}
                                                                {q.status === UPLOAD_STATUS.ERROR ||
                                                                file.status === "ERROR" ? (
                                                                    <span
                                                                        className={"file-error"}
                                                                        data-tooltip={"Upload failed."}
                                                                    >
                                                                        <i className="sprite sprite-error"></i>
                                                                    </span>
                                                                ) : (
                                                                    ""
                                                                )}
                                                            </div>
                                                            <div className="file-status">
                                                                {(q.status === UPLOAD_STATUS.UPLOADING ||
                                                                    q.status === UPLOAD_STATUS.ERROR ||
                                                                    q.status === UPLOAD_STATUS.CANCELED ||
                                                                    q.status === UPLOAD_STATUS.COMPLETE) && (
                                                                    <React.Fragment>
                                                                        {q.status === UPLOAD_STATUS.UPLOADING &&
                                                                        file.uploadedPercentage < 1 &&
                                                                        file.status !== "ERROR" ? (
                                                                            <small>
                                                                                <i
                                                                                    className={
                                                                                        "sprite sprite-ingesting"
                                                                                    }
                                                                                ></i>{" "}
                                                                                {t(
                                                                                    `common::msg::Pending to upload...`,
                                                                                    `Pending to upload...`,
                                                                                )}
                                                                            </small>
                                                                        ) : (
                                                                            <ProgressBar
                                                                                status={q.status}
                                                                                length={1}
                                                                                className={"bg-secondary"}
                                                                                progress={file.uploadedPercentage}
                                                                            />
                                                                        )}
                                                                    </React.Fragment>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </React.Fragment>
                                                ))}
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </React.Fragment>
            ) : (
                ""
            )}
        </React.Fragment>
    );
};

export default withTranslation()(AssetUploadQueueList);
